/**
 * Pure forecast computation for the Finances module. Given the current list of
 * overhead expenses, payroll positions, and company-wide payroll settings,
 * project monthly cost over a window starting at `fromMonth`.
 *
 * Design notes
 * ────────────
 * - Recurring overhead is charged on CRM occurrence dates (recurringDay /
 *   weekly/biweekly cadence from startDate), not frequency-normalized
 *   monthly factors. One-time expenses contribute only on their startDate.
 * - Overhead rows with `scalesWith: 'per_active_employee'` are multiplied by
 *   the count of active payroll positions for that calendar month.
 * - Payroll loaded cost is broken out into real variables: base salary,
 *   SEP IRA, health reimbursement (taxable), employer taxes, additive PTO
 *   backfill (treats each PTO day as an extra day of pay covering overtime /
 *   contractor backfill), and a per-position "extras" $ amount.
 * - "Planned" positions still contribute to the forecast — that's the point of
 *   modeling them.
 */
import {
  OverheadExpense,
  OverheadCategory,
  OverheadFrequency,
  PayrollPosition,
  PayrollDepartment,
  PayrollStatus,
  PayrollSettings,
  DEFAULT_PAYROLL_SETTINGS,
  WORK_DAYS_PER_YEAR,
  CashflowMethodData,
  CashflowAccountType,
  DailyFinanceReport,
} from '../types/finances';
import {
  computeEmployerPayrollTaxes,
  resolveWorkState,
  ZERO_EMPLOYER_TAX_BREAKDOWN,
  type EmployerTaxBreakdown,
} from './payrollEmployerTax';
import { resolvePositionForMonth } from './payrollCompSchedule';
import {
  occurrenceAmount,
  overheadOccurrencesInRange,
} from './overheadDateFilter';

export type { EmployerTaxBreakdown };

export const MONTHLY_FACTOR: Record<OverheadFrequency, number> = {
  daily: 30.4167,
  weekly: 4.3333,
  biweekly: 2.1667,
  monthly: 1,
  quarterly: 1 / 3,
  annually: 1 / 12,
  /** Handled specially (only contributes in the calendar month it falls in). */
  'one-time': 0,
};

/** Itemized breakdown of one position's monthly loaded cost for a given month. */
export interface PositionMonthlyCost {
  salary: number;
  sepIra: number;
  healthReimbursement: number;
  employerTax: number;
  employerTaxBreakdown: EmployerTaxBreakdown;
  /** True when a legacy flat employerTaxRateOverride is in effect. */
  employerTaxIsFlatOverride: boolean;
  ptoDailyRate: number;
  ptoBackfill: number;
  extras: number;
  total: number;
}

export interface PositionCostContext {
  /** Linked CRM user location string — used to infer work state when position.workState is unset. */
  crmUserLocation?: string | null;
}

export const ZERO_POSITION_COST: PositionMonthlyCost = {
  salary: 0,
  sepIra: 0,
  healthReimbursement: 0,
  employerTax: 0,
  employerTaxBreakdown: { ...ZERO_EMPLOYER_TAX_BREAKDOWN },
  employerTaxIsFlatOverride: false,
  ptoDailyRate: 0,
  ptoBackfill: 0,
  extras: 0,
  total: 0,
};

export interface ForecastBucket {
  /** First day of the month at 00:00 local time. */
  month: Date;
  /** Convenience label: "Jan 2026". */
  label: string;
  /** Count of payroll positions active during this month. */
  activeHeadcount: number;
  overheadByCategory: Record<OverheadCategory, number>;
  payrollByDepartment: Record<PayrollDepartment, number>;
  /** Known recurring overhead only. */
  recurringOverheadTotal: number;
  /** Discretionary OpEx envelopes from financeSettings/opexBudgets. */
  budgetedOpexTotal: number;
  overheadTotal: number;
  payrollTotal: number;
  total: number;
}

export interface ForecastOptions {
  fromMonth: Date;
  monthsAhead: number;
  /** Monthly sum of discretionary OpEx envelopes. Defaults to 0. */
  budgetedOpexMonthly?: number;
}

const ALL_OVERHEAD_CATEGORIES: OverheadCategory[] = [
  'rent', 'software', 'utilities', 'insurance', 'marketing', 'services', 'loans', 'other',
];
const ALL_DEPARTMENTS: PayrollDepartment[] = [
  'shop', 'office', 'sales', 'engineering', 'leadership', 'other',
];

function emptyOverheadBuckets(): Record<OverheadCategory, number> {
  const m = {} as Record<OverheadCategory, number>;
  ALL_OVERHEAD_CATEGORIES.forEach(c => { m[c] = 0; });
  return m;
}

function emptyPayrollBuckets(): Record<PayrollDepartment, number> {
  const m = {} as Record<PayrollDepartment, number>;
  ALL_DEPARTMENTS.forEach(d => { m[d] = 0; });
  return m;
}

function startOfMonth(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  r.setDate(1);
  return r;
}

function endOfMonth(d: Date): Date {
  const r = startOfMonth(d);
  r.setMonth(r.getMonth() + 1);
  r.setMilliseconds(-1);
  return r;
}

function sameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function monthLabel(d: Date): string {
  return `${MONTH_LABELS[d.getMonth()]} ${d.getFullYear()}`;
}

/** Whether a position is "active" during a given calendar month. */
export function isPositionActiveInMonth(position: PayrollPosition, month: Date): boolean {
  if (!position.isActive) return false;
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const start = position.startDate ? new Date(position.startDate) : null;
  if (start && start > monthEnd) return false;
  const end = position.endDate ? new Date(position.endDate) : null;
  if (end && end < monthStart) return false;
  return true;
}

/** Count of positions active during the given month. */
export function activeHeadcountForMonth(positions: PayrollPosition[], month: Date): number {
  let n = 0;
  for (const p of positions) if (isPositionActiveInMonth(p, month)) n++;
  return n;
}

/**
 * Returns the monthly contribution of a single overhead expense for the given
 * calendar month. When the expense has `scalesWith === 'per_active_employee'`,
 * the result is multiplied by `activeHeadcount`.
 */
export function overheadMonthlyContribution(
  expense: OverheadExpense,
  month: Date,
  activeHeadcount = 0,
): number {
  if (!expense.isActive) return 0;

  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const occurrences = overheadOccurrencesInRange(expense, monthStart, monthEnd);
  if (occurrences.length === 0) return 0;
  return occurrenceAmount(expense, activeHeadcount) * occurrences.length;
}

/**
 * Itemized loaded monthly cost for a single position during a given month.
 * Returns the all-zero record when the position is not active for that month.
 */
export function positionMonthlyCost(
  position: PayrollPosition,
  month: Date,
  settings: PayrollSettings = DEFAULT_PAYROLL_SETTINGS,
  context: PositionCostContext = {},
): PositionMonthlyCost {
  if (!isPositionActiveInMonth(position, month)) return { ...ZERO_POSITION_COST };

  const resolved = resolvePositionForMonth(position, month);
  const salary = (resolved.annualSalary || 0) / 12;
  const sepRate = position.sepIraRateOverride ?? settings.sepIraRate;
  const healthReimbursement = position.healthReimbursementMonthly || 0;
  const ptoDays = position.ptoDaysPerYear ?? settings.defaultPtoDaysPerYear;
  const extras = position.monthlyEmployeeExtras || 0;

  const sepIra = salary * sepRate;
  // Health reimbursement is part of taxable income (the user clarified this).
  const taxable = salary + healthReimbursement;

  const workState = resolveWorkState(
    position.workState,
    context.crmUserLocation,
    settings.defaultWorkState ?? 'VA',
  );
  const hasTaxOverride = position.employerTaxRateOverride != null;
  const employerTaxResult = computeEmployerPayrollTaxes({
    monthlyTaxable: taxable,
    workState,
    flatOverrideRate: hasTaxOverride ? position.employerTaxRateOverride : null,
  });
  const employerTax = employerTaxResult.total;

  // Additive backfill: each PTO day is treated as a real extra day of base pay.
  const ptoDailyRate = (resolved.annualSalary || 0) / WORK_DAYS_PER_YEAR;
  const ptoBackfill = (ptoDailyRate * ptoDays) / 12;

  const total = salary + sepIra + healthReimbursement + employerTax + ptoBackfill + extras;
  return {
    salary,
    sepIra,
    healthReimbursement,
    employerTax,
    employerTaxBreakdown: employerTaxResult.breakdown,
    employerTaxIsFlatOverride: employerTaxResult.isFlatOverride,
    ptoDailyRate,
    ptoBackfill,
    extras,
    total,
  };
}

/**
 * Convenience: just the total. Kept for components that don't need the
 * breakdown (`OverheadTable` group totals, etc.).
 */
export function positionMonthlyContribution(
  position: PayrollPosition,
  month: Date,
  settings: PayrollSettings = DEFAULT_PAYROLL_SETTINGS,
  context: PositionCostContext = {},
): number {
  return positionMonthlyCost(position, month, settings, context).total;
}

/**
 * Project monthly burn out across `monthsAhead` months, broken down by overhead
 * category and payroll department. Per-employee overhead is multiplied by the
 * active headcount for the corresponding month.
 */
export function forecastMonthly(
  overhead: OverheadExpense[],
  positions: PayrollPosition[],
  settings: PayrollSettings,
  options: ForecastOptions,
): ForecastBucket[] {
  const out: ForecastBucket[] = [];
  const cursor = startOfMonth(options.fromMonth);

  for (let i = 0; i < options.monthsAhead; i++) {
    const month = new Date(cursor);
    month.setMonth(month.getMonth() + i);

    const activeHeadcount = activeHeadcountForMonth(positions, month);

    const overheadByCategory = emptyOverheadBuckets();
    let recurringOverheadTotal = 0;
    for (const e of overhead) {
      const c = overheadMonthlyContribution(e, month, activeHeadcount);
      if (c <= 0) continue;
      overheadByCategory[e.category] += c;
      recurringOverheadTotal += c;
    }
    const budgetedOpexTotal = Math.max(0, Number(options.budgetedOpexMonthly) || 0);
    const overheadTotal = recurringOverheadTotal + budgetedOpexTotal;

    const payrollByDepartment = emptyPayrollBuckets();
    let payrollTotal = 0;
    for (const p of positions) {
      const c = positionMonthlyContribution(p, month, settings);
      if (c <= 0) continue;
      payrollByDepartment[p.department] += c;
      payrollTotal += c;
    }

    out.push({
      month,
      label: monthLabel(month),
      activeHeadcount,
      overheadByCategory,
      payrollByDepartment,
      recurringOverheadTotal,
      budgetedOpexTotal,
      overheadTotal,
      payrollTotal,
      total: overheadTotal + payrollTotal,
    });
  }

  return out;
}

/**
 * Convenience: monthly burn for "right now" (current calendar month). Used by
 * the sidebar footer and several overview KPI cards.
 */
export function currentMonthlyBurn(
  overhead: OverheadExpense[],
  positions: PayrollPosition[],
  settings: PayrollSettings = DEFAULT_PAYROLL_SETTINGS,
  now: Date = new Date(),
  budgetedOpexMonthly = 0,
): {
  overhead: number;
  recurringOverhead: number;
  budgeted: number;
  payroll: number;
  total: number;
  activeHeadcount: number;
} {
  const [bucket] = forecastMonthly(overhead, positions, settings, {
    fromMonth: now,
    monthsAhead: 1,
    budgetedOpexMonthly,
  });
  if (!bucket) {
    return { overhead: 0, recurringOverhead: 0, budgeted: 0, payroll: 0, total: 0, activeHeadcount: 0 };
  }
  return {
    overhead: bucket.overheadTotal,
    recurringOverhead: bucket.recurringOverheadTotal,
    budgeted: bucket.budgetedOpexTotal,
    payroll: bucket.payrollTotal,
    total: bucket.total,
    activeHeadcount: bucket.activeHeadcount,
  };
}

export type PayrollMonthlyByStatus = Record<PayrollStatus, number>;

/** Loaded payroll for the calendar month, grouped by position status. */
export function payrollMonthlyByStatus(
  positions: PayrollPosition[],
  settings: PayrollSettings = DEFAULT_PAYROLL_SETTINGS,
  month: Date = new Date(),
): PayrollMonthlyByStatus {
  const out: PayrollMonthlyByStatus = { filled: 0, open: 0, planned: 0 };
  for (const p of positions) {
    const amount = positionMonthlyContribution(p, month, settings);
    if (amount <= 0) continue;
    const status: PayrollStatus =
      p.status === 'open' || p.status === 'planned' ? p.status : 'filled';
    out[status] += amount;
  }
  return out;
}

// ── Historical cashflow overlay ────────────────────────────────────────────
//
// Combines the cached QuickBooks P&L into a per-month array the ForecastView
// chart can splice in front of the projected buckets. The current month (MTD)
// is included; future months are excluded — `forecastMonthly()` owns those.

/** QuickBooks P&L expense category used for the marketing actuals line. */
export const QB_MARKETING_ACCOUNT_NAME = 'Ad Spend';

export interface ExpenseCategoryBreakdown {
  name: string;
  amount: number;
}

export interface CashflowOverlayPoint {
  /** ISO `YYYY-MM` (first day of the month). */
  monthKey: string;
  /** Calendar Date for the first of the month — drives label/sort. */
  month: Date;
  /** "Jan 2026" style label, matches `ForecastBucket.label`. */
  label: string;
  /** Actual revenue (Income + OtherIncome). */
  actualRevenue: number;
  /** Direct job / material costs from the QB P&L COGS section. */
  actualCogs: number;
  /** QB "Ad Spend" category — shown as its own chart line. */
  actualMarketing: number;
  /** QB operating expenses excluding Ad Spend (not the CRM overhead model). */
  actualOverhead: number;
  /** COGS + operating expense. Convenience total for KPIs. */
  actualExpense: number;
  /** Net = revenue − expense. */
  actualNet: number;
  /** Major QB expense category subtotals for tooltip drill-down. */
  expenseBreakdown: ExpenseCategoryBreakdown[];
  /** True when this point is the in-progress current calendar month (MTD). */
  isCurrentMonth: boolean;
}

function accountMonthAmount(
  account: { monthly: Record<string, number> },
  monthKey: string,
): number {
  return account.monthly[monthKey] ?? 0;
}

/** Strip leading account numbers so "6000 Ad Spend" still matches. */
export function normalizeAccountLabel(name: string): string {
  return name.trim().replace(/^\d+\s*[.-]?\s*/, '');
}

function isMarketingAccountName(name: string): boolean {
  return normalizeAccountLabel(name).toLowerCase() === QB_MARKETING_ACCOUNT_NAME.toLowerCase();
}

/** Sum Ad Spend for a month from cached account lines. */
export function marketingForMonth(
  cache: CashflowMethodData,
  monthKey: string,
): number {
  const category = cache.accounts.find(
    a => a.isCategoryTotal && isMarketingAccountName(a.name),
  );
  if (category) return accountMonthAmount(category, monthKey);

  const directLeaves = cache.accounts.filter(
    a => !a.isCategoryTotal && isMarketingAccountName(a.name),
  );
  const directSum = directLeaves.reduce((sum, a) => sum + accountMonthAmount(a, monthKey), 0);
  if (directSum) return directSum;

  return cache.accounts
    .filter(a => !a.isCategoryTotal && a.parentExpenseSection && isMarketingAccountName(a.parentExpenseSection))
    .reduce((sum, a) => sum + accountMonthAmount(a, monthKey), 0);
}

/**
 * Major QB expense categories for a month. Prefers section subtotals when the
 * cache includes them; falls back to leaf expense accounts for older snapshots.
 */
export function expenseBreakdownForMonth(
  cache: CashflowMethodData,
  monthKey: string,
): ExpenseCategoryBreakdown[] {
  const expenseTypes = new Set<CashflowAccountType>(['Expense', 'OtherExpense']);
  const expenseAccounts = cache.accounts.filter(a => expenseTypes.has(a.accountType));
  const categoryTotals = expenseAccounts.filter(a => a.isCategoryTotal);
  const hasCategoryTotalsForMonth = categoryTotals.some(
    a => Math.abs(accountMonthAmount(a, monthKey)) > 0.005,
  );
  const source = hasCategoryTotalsForMonth ? categoryTotals : expenseAccounts.filter(a => !a.isCategoryTotal);

  return source
    .map(a => ({ name: a.name, amount: accountMonthAmount(a, monthKey) }))
    .filter(x => Math.abs(x.amount) > 0.005)
    .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
}

function parseMonthKey(key: string): Date | null {
  // Expected YYYY-MM. Be defensive.
  const m = /^(\d{4})-(\d{2})/.exec(key);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null;
  return new Date(year, month - 1, 1);
}

export function toMonthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export type ForecastChartTimelineMonth = {
  monthKey: string;
  month: Date;
  label: string;
  isCurrentMonth: boolean;
  /** Completed calendar month strictly before the current month. */
  isPast: boolean;
};

/**
 * Fixed chart window: `horizon` completed months before current, then `horizon`
 * months from the current month inclusive (actuals + forecast).
 */
export function buildForecastChartTimeline(
  horizon: number,
  now: Date = new Date(),
): ForecastChartTimelineMonth[] {
  const cur = startOfMonth(now);
  const currentMonthKey = toMonthKey(cur);
  const rows: ForecastChartTimelineMonth[] = [];

  for (let i = horizon; i >= 1; i--) {
    const month = new Date(cur);
    month.setMonth(month.getMonth() - i);
    rows.push({
      monthKey: toMonthKey(month),
      month,
      label: monthLabel(month),
      isCurrentMonth: false,
      isPast: true,
    });
  }

  for (let i = 0; i < horizon; i++) {
    const month = new Date(cur);
    month.setMonth(month.getMonth() + i);
    const monthKey = toMonthKey(month);
    rows.push({
      monthKey,
      month,
      label: monthLabel(month),
      isCurrentMonth: monthKey === currentMonthKey,
      isPast: false,
    });
  }

  return rows;
}

/**
 * Project a cached `CashflowMethodData` into chronological overlay points.
 *
 * Includes the current month (month-to-date). Months after the current month
 * are dropped so forecast buckets remain the source of truth going forward.
 *
 * @param cache Either `cache.cash` or `cache.accrual` from the Firestore cache.
 * @param now Reference "now" timestamp (defaults to new Date()).
 * @param maxMonthsBack Truncate to the most-recent N months (defaults to all).
 */
export function buildOverlayPoints(
  cache: CashflowMethodData | null | undefined,
  now: Date = new Date(),
  maxMonthsBack: number = Infinity,
): CashflowOverlayPoint[] {
  if (!cache || !cache.months?.length) return [];

  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const points: CashflowOverlayPoint[] = [];
  for (const key of cache.months) {
    if (key > currentMonthKey) continue;
    const month = parseMonthKey(key);
    if (!month) continue;
    const totals = cache.totals?.[key];
    if (!totals) continue;
    const revenue = totals.income || 0;
    const cogs = totals.cogs || 0;
    const marketing = marketingForMonth(cache, key);
    const overhead = Math.max(0, (totals.expense || 0) - marketing);
    const expense = cogs + marketing + overhead;
    points.push({
      monthKey: key,
      month,
      label: monthLabel(month),
      actualRevenue: revenue,
      actualCogs: cogs,
      actualMarketing: marketing,
      actualOverhead: overhead,
      actualExpense: expense,
      actualNet: revenue - expense,
      expenseBreakdown: expenseBreakdownForMonth(cache, key),
      isCurrentMonth: key === currentMonthKey,
    });
  }

  points.sort((a, b) => a.month.getTime() - b.month.getTime());
  if (Number.isFinite(maxMonthsBack) && points.length > maxMonthsBack) {
    return points.slice(points.length - maxMonthsBack);
  }
  return points;
}

export type CashBalanceMonthPoint = {
  monthKey: string;
  balance: number;
  /** Last daily snapshot used for this month, YYYY-MM-DD. */
  asOfDateKey: string;
};

/**
 * Last Sequence pod balance snapshot per calendar month from the daily finance report.
 */
export function buildCashBalanceByMonthKey(
  report: Pick<DailyFinanceReport, 'history' | 'sequencePodAmount' | 'dateKey'> | null | undefined,
): Record<string, CashBalanceMonthPoint> {
  const byMonth: Record<string, CashBalanceMonthPoint> = {};
  if (!report) return byMonth;

  for (const [dateKey, entry] of Object.entries(report.history || {})) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) continue;
    if (typeof entry?.sequencePodAmount !== 'number' || !Number.isFinite(entry.sequencePodAmount)) continue;
    const monthKey = dateKey.slice(0, 7);
    const existing = byMonth[monthKey];
    if (!existing || dateKey > existing.asOfDateKey) {
      byMonth[monthKey] = { monthKey, balance: entry.sequencePodAmount, asOfDateKey: dateKey };
    }
  }

  if (report.dateKey && typeof report.sequencePodAmount === 'number' && Number.isFinite(report.sequencePodAmount)) {
    const monthKey = report.dateKey.slice(0, 7);
    const existing = byMonth[monthKey];
    if (!existing || report.dateKey >= existing.asOfDateKey) {
      byMonth[monthKey] = {
        monthKey,
        balance: report.sequencePodAmount,
        asOfDateKey: report.dateKey,
      };
    }
  }

  return byMonth;
}

/**
 * Average monthly actual expense over the last N months that have data.
 * Returns 0 when the cache is empty.
 */
export function averageMonthlyActuals(
  cache: CashflowMethodData | null | undefined,
  monthsBack = 12,
  now: Date = new Date(),
): { revenue: number; cogs: number; marketing: number; overhead: number; expense: number; net: number; months: number } {
  const points = buildOverlayPoints(cache, now, monthsBack);
  if (!points.length) {
    return { revenue: 0, cogs: 0, marketing: 0, overhead: 0, expense: 0, net: 0, months: 0 };
  }
  const n = points.length;
  const revenue = points.reduce((s, p) => s + p.actualRevenue, 0) / n;
  const cogs = points.reduce((s, p) => s + p.actualCogs, 0) / n;
  const marketing = points.reduce((s, p) => s + p.actualMarketing, 0) / n;
  const overhead = points.reduce((s, p) => s + p.actualOverhead, 0) / n;
  const expense = points.reduce((s, p) => s + p.actualExpense, 0) / n;
  return { revenue, cogs, marketing, overhead, expense, net: revenue - expense, months: n };
}
